import { FieldDef } from '@core/models';
import { RelatedToType } from '@shared/enums';
import { CustomFormField, isFlatCustomFieldArray } from '../custom-form.types';

export const ADDRESS_PART_FIELDS = new Set([
  'address_string',
  'street_address',
  'city',
  'state',
  'postal_code',
  'latitude',
  'longitude',
]);

export const ADDRESS_COMPOSITE_FIELDS = new Set(['cff_address', 'address_string', 'cff_add_properties']);

export const PROPERTY_ADD_FORM_FIELDS_BLACKLIST = ['project_ids', 'title', ...Array.from(ADDRESS_PART_FIELDS)];

const isAddressRelated = (f: CustomFormField): boolean => ADDRESS_PART_FIELDS.has(f.name) || f.name === 'cff_address';

export function buildSelectedFieldsForDrawer(
  selectedFields: CustomFormField[] | CustomFormField[][] | null | undefined,
  relatedToType: RelatedToType,
): CustomFormField[] | CustomFormField[][] {
  const crmObjectTypes = [RelatedToType.Company, RelatedToType.Contact];
  const allowedObjectsForCffAddress = [...crmObjectTypes, RelatedToType.Property];
  const templateIdField: CustomFormField = { name: 'template_id', isRequired: true, isRemovable: false };
  const isCrmObject = [...crmObjectTypes].includes(relatedToType);
  const cffAddress: CustomFormField = { name: 'cff_address', isRequired: !isCrmObject, isRemovable: isCrmObject };
  const fields = selectedFields ?? [];

  if (fields.length > 0 && !isFlatCustomFieldArray(fields)) {
    const rowsWithTemplateId = ensureTemplateIdInRows(fields, templateIdField);

    if (!allowedObjectsForCffAddress.includes(relatedToType)) {
      return rowsWithTemplateId;
    }

    return buildFromRows(rowsWithTemplateId, cffAddress);
  }

  const flatFields = ensureTemplateIdInFlat(fields as CustomFormField[], templateIdField);

  if (!allowedObjectsForCffAddress.includes(relatedToType)) {
    return flatFields;
  }

  return buildFromFlat(flatFields, cffAddress);
}

function buildFromFlat(fields: CustomFormField[], cffAddress: CustomFormField): CustomFormField[] {
  if (fields.some((f) => f.name === 'cff_address')) {
    return fields
      .filter((f) => !ADDRESS_PART_FIELDS.has(f.name))
      .map((field) => (field.name === 'cff_address' ? cffAddress : field));
  }

  const filtered = fields.filter((f) => !isAddressRelated(f));

  const templateIdIndex = filtered.findIndex((f) => f.name === 'template_id');
  const nameIndex = filtered.findIndex((f) => f.name === 'name');
  const insertAt = templateIdIndex >= 0 ? templateIdIndex + 1 : nameIndex >= 0 ? nameIndex + 1 : 0;

  return [...filtered.slice(0, insertAt), cffAddress, ...filtered.slice(insertAt)];
}

function buildFromRows(rows: CustomFormField[][], cffAddress: CustomFormField): CustomFormField[][] {
  if (rows.some((row) => row.some((f) => f.name === 'cff_address'))) {
    return rows
      .map((row) =>
        row
          .filter((f) => !ADDRESS_PART_FIELDS.has(f.name))
          .map((field) => (field.name === 'cff_address' ? cffAddress : field)),
      )
      .filter((row) => row.length > 0);
  }

  const filtered = rows.map((row) => row.filter((f) => !isAddressRelated(f))).filter((row) => row.length > 0);

  let insertAtRow = 0;
  for (let i = 0; i < filtered.length; i++) {
    if (filtered[i].some((f) => f.name === 'template_id' || f.name === 'name')) {
      insertAtRow = i + 1;
    }
  }

  return [...filtered.slice(0, insertAtRow), [cffAddress], ...filtered.slice(insertAtRow)];
}

function ensureTemplateIdInFlat(fields: CustomFormField[], templateIdField: CustomFormField): CustomFormField[] {
  if (fields.some((f) => f.name === 'template_id')) {
    return fields.map((field) => (field.name === 'template_id' ? templateIdField : field));
  }

  const nameIndex = fields.findIndex((f) => f.name === 'name');
  const insertAt = nameIndex >= 0 ? nameIndex + 1 : 0;

  return [...fields.slice(0, insertAt), templateIdField, ...fields.slice(insertAt)];
}

function ensureTemplateIdInRows(rows: CustomFormField[][], templateIdField: CustomFormField): CustomFormField[][] {
  if (rows.some((row) => row.some((f) => f.name === 'template_id'))) {
    return rows.map((row) => row.map((field) => (field.name === 'template_id' ? templateIdField : field)));
  }

  let insertAtRow = 0;
  for (let i = 0; i < rows.length; i++) {
    if (rows[i].some((f) => f.name === 'name')) {
      insertAtRow = i + 1;
    }
  }

  return [...rows.slice(0, insertAtRow), [templateIdField], ...rows.slice(insertAtRow)];
}

export function getCustomAddressWithCoordinatesFieldDef(
  relatedToType: RelatedToType,
): Omit<
  FieldDef,
  | 'account_id'
  | 'created_at'
  | 'created_by_id'
  | 'created_by_user_name'
  | 'custom_object_id'
  | 'import'
  | 'locked'
  | 'meta'
  | 'related_to_id'
  | 'displayGroupedType'
  | 'val'
  | 'required_for_import'
  | 'usedForTimeSeries'
  | '_parentFieldName'
  | '_parentFieldLabel'
  | '_parentFieldMeta'
  | 'task_role_id'
  | 'tsTableOptions'
  | 'conditional_styling'
> {
  const isRequired = relatedToType === RelatedToType.Property;
  return {
    name: 'cff_address',
    label: 'Custom Address with Coordinates',
    type: 'text',
    related_to_type: relatedToType,
    isRemovable: !isRequired,
    isRequired,
  };
}
